import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { DataElementRequestType } from "../services/data-service";

export interface RequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    requestType: DataElementRequestType;
    description: string;
    reason?: string;
  }) => Promise<void>;
  elementName: string;
  elementKey: string;
  submitting?: boolean;
}

const REQUEST_TYPES: Array<{ value: DataElementRequestType; label: string }> = [
  { value: "DATA_ACCESS", label: "Access my data" },
  { value: "DATA_CORRECTION", label: "Correct my data" },
  { value: "DATA_ERASURE", label: "Erase my data" },
  { value: "DATA_PORTABILITY", label: "Port my data" },
];

export const RequestDialog: React.FC<RequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
  elementName,
  elementKey,
  submitting = false,
}) => {
  const [requestType, setRequestType] = useState<DataElementRequestType>("DATA_ACCESS");
  const [description, setDescription] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const isValid = useMemo(() => {
    return requestType !== undefined && description.trim().length > 0;
  }, [requestType, description]);

  const handleSubmit = async () => {
    setTouched({ requestType: true, description: true });
    if (!isValid || submitting) return;
    await onSubmit({ requestType, description: description.trim(), reason: reason.trim() || undefined });
  };

  const resetOnClose = () => {
    setRequestType("DATA_ACCESS");
    setDescription("");
    setReason("");
    setTouched({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={resetOnClose} fullWidth maxWidth="sm">
      <DialogTitle>Create a request</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            This request will be scoped to: <strong>{elementName}</strong> ({elementKey})
          </Typography>
          <TextField
            select
            label="Request type"
            value={requestType}
            onChange={(e) => setRequestType(e.target.value as DataElementRequestType)}
            error={touched.requestType && !requestType}
            helperText={touched.requestType && !requestType ? "Please choose a request type" : " "}
          >
            {REQUEST_TYPES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            placeholder="Describe your request so we can process it quickly"
            onBlur={() => setTouched((t) => ({ ...t, description: true }))}
            error={touched.description && description.trim().length === 0}
            helperText={
              touched.description && description.trim().length === 0 ? "Description is required" : " "
            }
          />
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            placeholder="Provide any additional context if needed"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetOnClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid || submitting}>
          {submitting ? "Submitting…" : "Submit request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDialog;

